import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import {
  Route,
  Calendar as CalendarIcon,
  MapPin,
  User,
  Truck,
  Plus,
  RefreshCw,
  ChevronRight,
  Package,
} from "lucide-react";
import { format, addDays, startOfDay } from "date-fns";
import { cn } from "@/lib/utils";

interface RoutePlan {
  id: string;
  routeNo: string;
  date: string;
  cityId: string;
  cityName: string;
  zoneId: string | null;
  zoneName: string | null;
  assignedRiderId: string | null;
  riderName: string | null;
  status: string;
  stopsCount: number;
  plannedStartAt: string | null;
  plannedEndAt: string | null;
}

interface City {
  id: string;
  name: string;
  zones: Array<{ id: string; name: string }>;
}

interface EligibilityData {
  date: string;
  cityId: string;
  zoneId: string | null;
  eligibleShipments: number;
  availableRiders: number;
}

export default function AdminRoutesPage() {
  const [routes, setRoutes] = useState<RoutePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(addDays(new Date(), 1));
  const [selectedCity, setSelectedCity] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [genDate, setGenDate] = useState<Date>(addDays(new Date(), 1));
  const [genCity, setGenCity] = useState<string>("");
  const [genZone, setGenZone] = useState<string>("all");
  const [genMode, setGenMode] = useState<"AUTO" | "MANUAL">("AUTO");
  const [generating, setGenerating] = useState(false);
  const [eligibility, setEligibility] = useState<EligibilityData | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  useEffect(() => {
    fetchRoutes();
    fetchCities();
  }, [selectedDate, selectedCity, selectedStatus]);

  useEffect(() => {
    if (generateDialogOpen && genCity) {
      checkEligibility();
    }
  }, [generateDialogOpen, genDate, genCity, genZone]);

  async function fetchRoutes() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedDate) {
        params.set("date", format(selectedDate, "yyyy-MM-dd"));
      }
      if (selectedCity !== "all") {
        params.set("cityId", selectedCity);
      }
      if (selectedStatus !== "all") {
        params.set("status", selectedStatus);
      }
      const res = await fetch(`/api/admin/routes?${params}`);
      const data = await res.json();
      setRoutes(data.routes || []);
    } catch (error) {
      console.error("Error fetching routes:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCities() {
    try {
      const res = await fetch("/api/admin/areas");
      const data = await res.json();
      const cityMap = new Map<string, City>();
      for (const area of data.areas || []) {
        if (area.cityId && area.cityName) {
          if (!cityMap.has(area.cityId)) {
            cityMap.set(area.cityId, {
              id: area.cityId,
              name: area.cityName,
              zones: [],
            });
          }
          if (area.zoneId && area.zoneName) {
            const city = cityMap.get(area.cityId)!;
            if (!city.zones.find(z => z.id === area.zoneId)) {
              city.zones.push({ id: area.zoneId, name: area.zoneName });
            }
          }
        }
      }
      setCities(Array.from(cityMap.values()));
      if (cityMap.size > 0 && !genCity) {
        setGenCity(Array.from(cityMap.values())[0].id);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  }

  async function checkEligibility() {
    if (!genCity) return;
    setCheckingEligibility(true);
    try {
      const params = new URLSearchParams({
        date: format(genDate, "yyyy-MM-dd"),
        cityId: genCity,
      });
      if (genZone !== "all") {
        params.set("zoneId", genZone);
      }
      const res = await fetch(`/api/admin/routes/generate?${params}`);
      const data = await res.json();
      setEligibility(data);
    } catch (error) {
      console.error("Error checking eligibility:", error);
    } finally {
      setCheckingEligibility(false);
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      const res = await fetch("/api/admin/routes/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: format(genDate, "yyyy-MM-dd"),
          cityId: genCity,
          zoneId: genZone !== "all" ? genZone : undefined,
          mode: genMode,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setGenerateDialogOpen(false);
        setSelectedDate(genDate);
        setSelectedCity(genCity);
        fetchRoutes();
      } else {
        alert(data.error || "Failed to generate routes");
      }
    } catch (error) {
      console.error("Error generating routes:", error);
    } finally {
      setGenerating(false);
    }
  }

  const selectedCityData = cities.find(c => c.id === genCity);

  return (
    <div>
      <PageHeader
        title="Route Planning"
        subtitle="Generate and manage delivery routes"
        actions={
          <Button onClick={() => setGenerateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Generate Routes
          </Button>
        }
      />

      <div className="flex flex-wrap gap-4 mb-6">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {format(selectedDate, "PPP")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Select value={selectedCity} onValueChange={setSelectedCity}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map((city) => (
              <SelectItem key={city.id} value={city.id}>
                {city.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="PUBLISHED">Published</SelectItem>
            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="icon" onClick={fetchRoutes}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-6 w-32 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : routes.length === 0 ? (
        <EmptyState
          icon={Route}
          title="No routes found"
          description="Generate routes for the selected date and city"
          action={
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Generate Routes
            </Button>
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {routes.map((route) => (
            <Link key={route.id} to={`/admin/routes/${route.id}`}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">
                      {route.routeNo}
                    </CardTitle>
                    <StatusBadge status={route.status.toLowerCase() as any} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{format(new Date(route.date), "EEE, MMM d")}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {route.cityName}
                        {route.zoneName && ` - ${route.zoneName}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Package className="h-4 w-4" />
                      <span>{route.stopsCount} stops</span>
                    </div>
                    {route.riderName ? (
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-green-600" />
                        <span className="font-medium">{route.riderName}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-amber-600">
                        <User className="h-4 w-4" />
                        <span>Unassigned</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end mt-4 text-primary">
                    <span className="text-sm font-medium">View Details</span>
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Generate Routes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !genDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(genDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={genDate}
                    onSelect={(date) => date && setGenDate(date)}
                    disabled={(date) => date < startOfDay(new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">City</label>
              <Select value={genCity} onValueChange={setGenCity}>
                <SelectTrigger>
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedCityData && selectedCityData.zones.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Zone (Optional)</label>
                <Select value={genZone} onValueChange={setGenZone}>
                  <SelectTrigger>
                    <SelectValue placeholder="All zones" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Zones</SelectItem>
                    {selectedCityData.zones.map((zone) => (
                      <SelectItem key={zone.id} value={zone.id}>
                        {zone.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Assignment Mode</label>
              <Select value={genMode} onValueChange={(v) => setGenMode(v as "AUTO" | "MANUAL")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUTO">Auto-assign riders</SelectItem>
                  <SelectItem value="MANUAL">Manual assignment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {eligibility && (
              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Eligible Shipments</p>
                      <p className="text-2xl font-bold">{eligibility.eligibleShipments}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Available Riders</p>
                      <p className="text-2xl font-bold">{eligibility.availableRiders}</p>
                    </div>
                  </div>
                  {eligibility.eligibleShipments === 0 && (
                    <p className="text-sm text-amber-600 mt-2">
                      No shipments scheduled for this date/location
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || !genCity || (eligibility?.eligibleShipments === 0)}
            >
              {generating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Truck className="h-4 w-4 mr-2" />
                  Generate Routes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
